import React, { useState, useCallback } from 'react';
import {
    Collapse,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';

const listIconSx = { color: '#000' };
const menuIconFontSize = 'small';
import { LAYOUT_CONSTANTS, MENU_SECTIONS } from './constants';

/**
 * 사이드바 네비게이션 컴포넌트
 * 
 * 애플리케이션의 사이드바 네비게이션 메뉴를 렌더링합니다.
 * 
 * @param {Object} props
 * @param {Function} props.onNavigate - 페이지 이동 핸들러 함수 (path: string) => void
 * @param {string} props.minHeight - 사이드바 최소 높이 (CSS 값)
 * 
 * @example
 * <Sidebar 
 *   onNavigate={(path) => navigate(path)} 
 *   minHeight="calc(100vh - 50px)" 
 * />
 */
const Sidebar = ({ onNavigate, minHeight }) => {
    // 메뉴 열림/닫힘 상태 관리
    const [menuState, setMenuState] = useState(
        MENU_SECTIONS.reduce((acc, section) => {
            acc[section.key] = false;
            return acc;
        }, {})
    );

    /**
     * 메뉴 토글 핸들러 (범용)
     * @param {string} menuKey - 토글할 메뉴 키 ('users' | 'education' | 'video')
     */
    const handleMenuToggle = useCallback((menuKey) => {
        setMenuState((prev) => ({
            ...prev,
            [menuKey]: !prev[menuKey],
        }));
    }, []);

    /**
     * 페이지로 이동 핸들러
     * @param {string} path - 이동할 경로
     */
    const handleGoPage = useCallback(
        (path) => {
            onNavigate(path);
        },
        [onNavigate]
    );

    return (
        <Paper
            sx={{
                width: LAYOUT_CONSTANTS.WIDTH,
                minHeight: minHeight,
                height: 'auto',
            }}
        >
            <List
                sx={{
                    width: '100%',
                    maxWidth: 260,
                    bgcolor: 'transparent',
                    marginTop: '5px',
                    color: '#333',
                }}
                component='nav'
            >
                {/* 메뉴 섹션 렌더링 */}
                {MENU_SECTIONS.map((section) => {
                    const SectionIcon = section.icon;
                    return (
                        <React.Fragment key={section.key}>
                            <ListItemButton onClick={() => handleMenuToggle(section.key)}>
                                <ListItemIcon>
                                    <SectionIcon fontSize={menuIconFontSize} sx={listIconSx} />
                                </ListItemIcon>
                                <ListItemText primary={<b>{section.title}</b>} />
                                {menuState[section.key] ? <ExpandLess /> : <ExpandMore />}
                            </ListItemButton>

                            <Collapse in={menuState[section.key]} timeout='auto' unmountOnExit>
                                <List component='div' disablePadding>
                                    {section.items.map((item, index) => {
                                        const ItemIcon = item.icon;
                                        return (
                                            <ListItemButton
                                                key={index}
                                                sx={{ pl: 4 }}
                                                onClick={() => handleGoPage(item.path)}
                                            >
                                                {ItemIcon && (
                                                    <ListItemIcon>
                                                        <ItemIcon fontSize={menuIconFontSize} sx={listIconSx} />
                                                    </ListItemIcon>
                                                )}
                                                <ListItemText primary={item.label} />
                                            </ListItemButton>
                                        );
                                    })}
                                </List>
                            </Collapse>
                        </React.Fragment>
                    );
                })}
            </List>
        </Paper>
    );
};

export default Sidebar;
